import React from 'react';
import { UncontrolledTable } from '~/components';
import { CardSubtitle } from 'reactstrap';
import { useParams } from 'react-router-dom';
import usePatient from '~/utils/patient/hook';
import { hasLegalStatus } from '~/utils/patient/identity-access';

import { TableFilterIdentitySource } from '~/components/Filters';
import { useIdentityPage } from '~/utils/identity/page';
import { MatchStatus } from '~/utils/const/identity';

import PrintButton from '../printButton';

const HealthcareMedicaid = () => {
  const params = useParams();
  const [, patient] = usePatient();

  const {
    setPrintSpec,
    setIsPrintingStaged,
  } = useIdentityPage();

  // Manually build print action for this as
  // it is an edge case. If needing to do this
  // more than once, look at expanding the identity
  // page context / provider.
  const setupPrint = () => {
    setPrintSpec({
      'Primary Contacts': {
        pageItems: [{
          title: 'Healthcare / Medicaid',
          specificItem: 'Medicaid',
        }],
      },
    });
    setIsPrintingStaged(true);
  };

  return (
    <React.Fragment>
      <div>
        <CardSubtitle tag="h5" className="m-3">
          Primary Care Provider
        </CardSubtitle>

        <UncontrolledTable
          className="mx-4"
          defaultPageSize={1}
          showPaginationBottom={false}
          filterable={false}
          endpoint={{
          url: 'data/public/list_view_cs_tbl_primaryprovider',
          params: {
            patient_uid: params.patient_id,
            iscurrentpcp: 'current',
            match_status_id: MatchStatus.Approved,
            'effdateDt__!': 'null',
            page: 1,
            'page-size': 1,
          },
        }}
          defaultSorted={[{ id: 'effdateDt', desc: true }]}
          columns={[
          {
            id: 'lastname',
            Header: 'Name',
            accessor: d => `${d.attributes.lastname}, ${d.attributes.firstname}`,
          },
          {
            id: 'sourceDescription',
            Header: 'Reporting Healthcare Institution',
            accessor: 'attributes.sourceDescription',
            Filter: d => <TableFilterIdentitySource {...d} />,
          },
          {
            id: 'phone',
            Header: 'Phone',
            accessor: 'attributes.phone',
            cellType: 'tel',
          },
          {
            id: 'address',
            Header: 'Address',
            accessor: d => (
              <>
                <span>{d.attributes.address}</span>
                <br />
                <span>
                  {d.attributes.city} {d.attributes.state} {d.attributes.zip}
                </span>
              </>
            ),
          },
          {
            id: 'effdateDt',
            Header: 'Date Identified',
            accessor: 'attributes.effdateDt',
            cellType: 'date',
          },
        ]}
        />
      </div>

      <CardSubtitle tag="h5" className="m-3">
        Medicaid
        { hasLegalStatus(patient) &&
        <PrintButton
          logTitle="Medicaid"
          disableExpansion
          onClick={setupPrint}
        />}
      </CardSubtitle>

      <UncontrolledTable
        name="mcp"
        className="mx-4"
        endpoint={{
          url: 'data/public/tbl_mcp',
          params: {
            patient_uid: params.patient_id,
            display: 'true',
            distinct: 'sacwis_person_id,medicaid_num,medicaid_num,mcp_enrollment_date',
            'all-data': '1',
          },
        }}
        defaultSorted={[{ id: 'mcpEnrollmentDate', desc: true }]}
        columns={[
          {
            id: 'medicaidNum',
            Header: 'Medicaid Number',
            headerTooltip: 'Medicaid billing number provided by SACWIS',
            accessor: 'attributes.medicaidNum',
          },
          {
            id: 'currentMcp',
            Header: 'Managed Care Plan',
            headerTooltip: 'Managed Care Plan for child on Medicaid (Same as Managed Care Provider)',
            accessor: 'attributes.mcpName',
          },
          {
            id: 'mcpEnrollmentDate',
            Header: 'Enrollment Date',
            accessor: 'attributes.mcpEnrollmentDate',
            cellType: 'date',
          },
        ]}
      />
    </React.Fragment>
  );
};

export default HealthcareMedicaid;
